import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Droplets,
  Cloud,
  CloudRain,
  Cpu,
  Radio,
  Calendar,
  Zap,
  Bell,
  MapPin,
  Mail,
  ExternalLink,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const SWAT_PRIVACY_URL = 'https://swat-id.com/Privacy';
const SWAT_LEGAL_URL = 'https://swat-id.com/Legal';

/** Capturas de la app para la sección "La aplicación en acción". Ruta desde public/landing/ → /landing/xxx */
const LANDING_SCREENSHOTS = [
  { id: 'aemet', src: '/landing/AEMET.png', title: 'Datos AEMET', subtitle: 'Previsión y datos para riego inteligente', icon: CloudRain },
  { id: 'dashboard', src: '/landing/Dashboard.png', title: 'Dashboard', subtitle: 'Resumen y estado en tiempo real', icon: Droplets },
  { id: 'controllers', src: '/landing/Controladoras.png', title: 'Controladoras', subtitle: 'Estado, modo y válvulas', icon: Cpu },
  { id: 'sensors', src: '/landing/Sensores.png', title: 'Sensores', subtitle: 'Lecturas y ubicación', icon: Radio },
  { id: 'schedules', src: '/landing/Programaciones.png', title: 'Programaciones', subtitle: 'Calendario y programación de riego', icon: Calendar },
] as const;

export default function Landing() {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  return (
    <div className="min-h-screen flex flex-col bg-[hsl(145,25%,96%)]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-primary/20 bg-[hsl(145,30%,97%)]/95 backdrop-blur supports-[backdrop-filter]:bg-[hsl(145,30%,97%)]/90">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-lg">
              <Droplets className="w-6 h-6 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-[hsl(152,45%,22%)]">IrriGo</span>
          </div>
          <nav className="flex items-center gap-2">
            <Button variant="ghost" asChild className="text-[hsl(152,45%,25%)] hover:bg-primary/10">
              <a href="#features">Características</a>
            </Button>
            <Button variant="ghost" asChild className="text-[hsl(152,45%,25%)] hover:bg-primary/10">
              <a href="#uso">Formas de uso</a>
            </Button>
            <Button asChild className="gradient-primary text-white border-0 hover:opacity-90">
              <Link to="/login">Iniciar sesión</Link>
            </Button>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero con fondo de riego */}
        <section className="relative overflow-hidden text-primary-foreground py-24 md:py-32">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(152,48%,28%)] via-[hsl(155,45%,32%)] to-[hsl(165,50%,38%)]" />
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 8c-4 6-8 16-8 24a8 8 0 1 0 16 0c0-8-4-18-8-24z' fill='%23fff'/%3E%3C/svg%3E")`,
          }} />
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/10 to-transparent" />
          <div className="container relative px-4 text-center">
            <p className="text-primary-foreground/90 text-sm md:text-base font-medium uppercase tracking-widest mb-4">
              Smart Irrigation · SWAT-ID
            </p>
            <h1 className="text-3xl font-display font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl max-w-4xl mx-auto drop-shadow-sm">
              GESTIÓN DE RIEGO AUTOMÁTICO
            </h1>
            <p className="mt-5 text-lg md:text-xl text-primary-foreground/95 max-w-2xl mx-auto leading-relaxed">
              Controla jardines, controladoras y sensores desde una única plataforma. Modo local, remoto e integración con AEMET para un riego inteligente y eficiente.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Button size="lg" asChild className="bg-white text-[hsl(152,45%,28%)] hover:bg-white/95 border-0 shadow-lg">
                <Link to="/login">Iniciar sesión</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-2 border-white/60 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm">
                <a href="#uso">Conocer más</a>
              </Button>
            </div>
            <a href="#uso" className="inline-flex mt-12 text-primary-foreground/90 hover:text-white transition-colors" aria-label="Bajar a formas de uso">
              <ChevronDown className="w-8 h-8 animate-bounce" />
            </a>
          </div>
        </section>

        {/* Formas de uso */}
        <section id="uso" className="py-16 md:py-24 border-b border-primary/10 bg-[hsl(145,22%,97%)]">
          <div className="container px-4">
            <h2 className="text-2xl font-display font-bold text-center text-[hsl(152,45%,22%)] sm:text-3xl">
              Formas de uso
            </h2>
            <p className="mt-2 text-center text-[hsl(152,30%,35%)] max-w-2xl mx-auto">
              Adaptamos el control a tu instalación: en sitio, desde la nube o con datos meteorológicos.
            </p>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              <div className="rounded-xl border border-primary/15 bg-[hsl(0,0%,100%)] p-6 shadow-card hover:shadow-card-hover transition-all">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Cpu className="w-6 h-6 text-primary" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg text-[hsl(152,45%,22%)]">Modo local</h3>
                <p className="mt-2 text-sm text-[hsl(152,25%,38%)]">
                  Control en sitio: las controladoras funcionan de forma autónoma con programaciones y triggers locales, sin depender de internet.
                </p>
              </div>
              <div className="rounded-xl border border-primary/15 bg-[hsl(0,0%,100%)] p-6 shadow-card hover:shadow-card-hover transition-all">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Cloud className="w-6 h-6 text-primary" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg text-[hsl(152,45%,22%)]">Modo remoto</h3>
                <p className="mt-2 text-sm text-[hsl(152,25%,38%)]">
                  Gestión desde la nube: configura programaciones, revisa estado de controladoras y sensores, y recibe alarmas desde cualquier dispositivo.
                </p>
              </div>
              <div className="rounded-xl border border-primary/15 bg-[hsl(0,0%,100%)] p-6 shadow-card hover:shadow-card-hover transition-all">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CloudRain className="w-6 h-6 text-primary" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg text-[hsl(152,45%,22%)]">Integración AEMET</h3>
                <p className="mt-2 text-sm text-[hsl(152,25%,38%)]">
                  Datos meteorológicos oficiales para ajustar el riego: evapotranspiración, precipitaciones y predicciones para un uso eficiente del agua.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pantallas de ejemplo: capturas reales con fallback si no existe la imagen */}
        <section className="py-16 md:py-24 bg-[hsl(145,20%,95%)]" aria-label="Capturas de la aplicación">
          <div className="container px-4">
            <h2 className="text-2xl font-display font-bold text-center text-[hsl(152,45%,22%)] sm:text-3xl">
              La aplicación en acción
            </h2>
            <p className="mt-2 text-center text-[hsl(152,25%,38%)] max-w-2xl mx-auto">
              Pantallas reales de la plataforma: dashboard, meteorología, controladoras, sensores y programaciones.
            </p>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {LANDING_SCREENSHOTS.map((item) => {
                const showPlaceholder = failedImages.has(item.id);
                return (
                  <article
                    key={item.id}
                    className="group rounded-2xl border border-primary/15 bg-[hsl(0,0%,100%)] overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300"
                  >
                    <div className="relative aspect-video bg-muted overflow-hidden">
                      {!showPlaceholder && (
                        <img
                          src={item.src}
                          alt={`Captura de la aplicación: ${item.title}`}
                          className="absolute inset-0 w-full h-full object-cover object-top rounded-t-2xl"
                          loading="lazy"
                          decoding="async"
                          onError={() => setFailedImages((prev) => new Set(prev).add(item.id))}
                        />
                      )}
                      <div
                        className={cn(
                          'absolute inset-0 flex items-center justify-center p-6 transition-opacity duration-200',
                          showPlaceholder ? 'opacity-100 bg-muted' : 'opacity-0 group-hover:opacity-100 bg-black/50'
                        )}
                        aria-hidden
                      >
                        <div className="text-center text-muted-foreground">
                          {showPlaceholder ? (
                            <>
                              <item.icon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <span className="text-sm font-medium">{item.title}</span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-white drop-shadow-md">{item.title}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t border-primary/10 bg-[hsl(0,0%,100%)]">
                      <p className="font-display font-semibold text-[hsl(152,45%,22%)]">{item.title}</p>
                      <p className="text-xs text-[hsl(152,25%,38%)] mt-0.5">{item.subtitle}</p>
                    </div>
                  </article>
                );
              })}
            </div>
            {failedImages.size > 0 && (
              <p className="mt-6 text-center text-xs text-[hsl(152,25%,38%)] max-w-xl mx-auto">
                Para un aspecto más profesional, añade capturas en <code className="bg-muted px-1 rounded">public/landing/</code> (aemet.png, dashboard.png, controllers.png, sensors.png, schedules.png). Ver <code className="bg-muted px-1 rounded">docs/V0.2/LANDING_IMAGENES.md</code>.
              </p>
            )}
          </div>
        </section>

        {/* Características principales */}
        <section id="features" className="py-16 md:py-24 border-b border-primary/10 bg-[hsl(145,22%,97%)]">
          <div className="container px-4">
            <h2 className="text-2xl font-display font-bold text-center text-[hsl(152,45%,22%)] sm:text-3xl">
              Características principales
            </h2>
            <p className="mt-2 text-center text-[hsl(152,25%,38%)] max-w-2xl mx-auto">
              Una plataforma completa para la gestión del riego
            </p>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { icon: Cpu, title: 'Gestión de controladoras', desc: 'Control de válvulas, modos de operación y programaciones por controladora.' },
                { icon: Radio, title: 'Sensores', desc: 'Visualización de sensores y lecturas para supervisar el estado del jardín.' },
                { icon: Calendar, title: 'Programaciones', desc: 'Crea y edita programaciones de riego con asignación a controladoras y válvulas.' },
                { icon: Zap, title: 'Triggers automáticos', desc: 'Reglas automáticas basadas en condiciones para activar el riego.' },
                { icon: CloudRain, title: 'Integración AEMET', desc: 'Datos meteorológicos oficiales para optimizar el consumo de agua.' },
                { icon: Bell, title: 'Alarmas', desc: 'Notificaciones y registro de eventos para mantener el control.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 rounded-xl border border-primary/15 bg-[hsl(0,0%,100%)] p-5 shadow-card hover:shadow-card-hover transition-all">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-[hsl(152,45%,22%)]">{item.title}</h3>
                    <p className="mt-1 text-sm text-[hsl(152,25%,38%)]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-16 md:py-24 bg-primary/10 border-b border-primary/15">
          <div className="container px-4 text-center">
            <h2 className="text-2xl font-display font-bold text-[hsl(152,45%,22%)] sm:text-3xl">
              Comience a gestionar su riego hoy mismo
            </h2>
            <p className="mt-3 text-[hsl(152,30%,35%)] max-w-xl mx-auto">
              Descubra cómo IrriGo puede ayudarle a controlar el riego de forma segura y eficiente.
            </p>
            <Button size="lg" asChild className="mt-6 gradient-primary text-white border-0">
              <Link to="/login">Comenzar ahora</Link>
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 md:py-16 bg-[hsl(152,35%,18%)] text-[hsl(145,25%,90%)]">
          <div className="container px-4">
            <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center">
                    <Droplets className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-display font-bold text-white">IrriGo</span>
                </div>
                <p className="mt-2 text-sm text-[hsl(145,20%,80%)]">
                  Gestión de riego automático por SWAT-ID
                </p>
              </div>
              <div>
                <h4 className="font-display font-semibold text-white">SWAT-ID</h4>
                <p className="mt-1 text-sm font-medium text-[hsl(145,20%,85%)]">Smart Solutions for Smart Cities</p>
                <p className="mt-2 text-xs text-[hsl(145,15%,75%)]">Servicios: Smart City, Smart Water, Smart Emergency, Smart Consultancy</p>
              </div>
              <div>
                <h4 className="font-display font-semibold text-white">Contacto</h4>
                <ul className="mt-2 space-y-1.5 text-sm text-[hsl(145,20%,85%)]">
                  <li className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Plaza Virgen del Pilar 2, Bajo<br />46191 Vilamarxant, Valencia (España)</span>
                  </li>
                  <li>
                    <a href="mailto:info@swat-id.com" className="inline-flex items-center gap-1 hover:text-[hsl(145,60%,75%)] transition-colors">
                      <Mail className="w-4 h-4" /> info@swat-id.com
                    </a>
                  </li>
                  <li>Administración: <a href="tel:+34722548278" className="hover:text-[hsl(145,60%,75%)] transition-colors">+34 722 54 82 78</a></li>
                  <li>Proyectos: <a href="tel:+34633448427" className="hover:text-[hsl(145,60%,75%)] transition-colors">+34 633 44 84 27</a></li>
                  <li>Comercial: <a href="tel:+34666496706" className="hover:text-[hsl(145,60%,75%)] transition-colors">+34 666 49 67 06</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-display font-semibold text-white">Legal</h4>
                <ul className="mt-2 space-y-2 text-sm">
                  <li>
                    <a
                      href={SWAT_PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[hsl(145,20%,85%)] hover:text-[hsl(145,60%,75%)] transition-colors"
                    >
                      Política de Privacidad <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a
                      href={SWAT_LEGAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[hsl(145,20%,85%)] hover:text-[hsl(145,60%,75%)] transition-colors"
                    >
                      Aviso Legal <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-white/10 text-center text-sm text-[hsl(145,15%,70%)]">
              © {new Date().getFullYear()} SWAT-ID · Smart World and Things, S.L.
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
